import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
  useTheme
} from '@mui/material';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Cell
} from 'recharts';
import { Layout } from '@layout/Layout';
import usePlanReportStore, { Period } from '@/store/hr/PlanReportStore';
import usePlanBoardStore from '@/store/hr/PlanBoardStore';
import PeriodSelector from '@components/hr/planReports/PeriodSelector';
import { formatRate } from '@components/hr/planReports/TaskStatusChip';

const BAR_COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6', '#06b6d4'];

type Granularity = 'day' | 'week' | 'month';

const AnalyticsPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const analytics = usePlanReportStore((s) => s.analytics);
  const loading = usePlanReportStore((s) => s.loading);
  const error = usePlanReportStore((s) => s.error);
  const fetchAnalytics = usePlanReportStore((s) => s.fetchAnalytics);

  const board = usePlanBoardStore((s) => s.board);
  const fetchBoard = usePlanBoardStore((s) => s.fetchBoard);

  const [period, setPeriod] = useState<Period | null>(null);
  const [granularity, setGranularity] = useState<Granularity>('day');
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (!board) fetchBoard({});
  }, [board, fetchBoard]);

  useEffect(() => {
    if (period) fetchAnalytics({ from: period.from, to: period.to, granularity });
  }, [period?.from, period?.to, granularity, fetchAnalytics]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePeriod = useCallback((p: Period) => setPeriod(p), []);

  const gridStroke = isDark ? '#444' : '#eee';
  const tooltipStyle = {
    backgroundColor: isDark ? '#1a1a2e' : '#fff',
    border: `1px solid ${isDark ? '#333' : '#e0e0e0'}`,
    borderRadius: 8
  };

  // Rate is null when nothing was resolved in a bucket. Recharts would draw null
  // as a gap, which is exactly right — a day with no verdicts has no rate, and
  // plotting it as 0% would invent a collapse that never happened.
  const trend = (analytics?.trend || []).map((t) => ({
    ...t,
    completion_pct: t.completion_rate === null ? null : Math.round(t.completion_rate * 100)
  }));

  const rollupChart = (rows: any[]) =>
    rows.map((r) => ({
      name: r.label,
      completion: r.completion_rate === null ? 0 : Math.round(r.completion_rate * 100),
      tasks: r.tasks_total
    }));

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Plan Analytics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            How completion is trending, and where the work actually sits.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3, alignItems: 'center' }}>
          <PeriodSelector today={board?.today} onChange={handlePeriod} />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={granularity}
            onChange={(_e, v) => v && setGranularity(v)}
            aria-label="Trend granularity"
          >
            <ToggleButton value="day" sx={{ textTransform: 'none' }}>Daily</ToggleButton>
            <ToggleButton value="week" sx={{ textTransform: 'none' }}>Weekly</ToggleButton>
            <ToggleButton value="month" sx={{ textTransform: 'none' }}>Monthly</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Card sx={{ borderRadius: 2, mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Completion trend{' '}
              <Typography component="span" variant="body2" color="text.secondary">
                (overall: {formatRate(analytics?.summary?.completion_rate)})
              </Typography>
            </Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={trend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="completionFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#4f46e5" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#4f46e5" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                  <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} unit="%" tick={{ fontSize: 11 }} />
                  <RTooltip
                    contentStyle={tooltipStyle}
                    formatter={(v: any) => (v === null ? '—' : `${v}%`)}
                  />
                  <Area
                    type="monotone"
                    dataKey="completion_pct"
                    name="Completion"
                    stroke="#4f46e5"
                    strokeWidth={2}
                    fill="url(#completionFill)"
                    connectNulls={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 2 }}>
          <Tabs value={tab} onChange={(_e, v) => setTab(v)} variant="scrollable" sx={{ px: 2, pt: 1 }}>
            <Tab label="By department" sx={{ textTransform: 'none' }} />
            <Tab label="By manager" sx={{ textTransform: 'none' }} />
            <Tab label="Workload" sx={{ textTransform: 'none' }} />
          </Tabs>
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={360}>
                <BarChart
                  layout="vertical"
                  data={
                    tab === 0
                      ? rollupChart(analytics?.byDepartment || [])
                      : tab === 1
                        ? rollupChart(analytics?.byManager || [])
                        : (analytics?.workload || []).slice(0, 20).map((w) => ({
                            name: w.name,
                            completion:
                              w.completion_rate === null ? 0 : Math.round(w.completion_rate * 100),
                            tasks: w.tasks_total
                          }))
                  }
                  margin={{ top: 8, right: 24, left: 8, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                  <RTooltip contentStyle={tooltipStyle} />
                  <Bar
                    dataKey={tab === 2 ? 'tasks' : 'completion'}
                    name={tab === 2 ? 'Tasks' : 'Completion %'}
                    radius={[0, 4, 4, 0]}
                  >
                    {(tab === 2
                      ? (analytics?.workload || []).slice(0, 20)
                      : tab === 0
                        ? analytics?.byDepartment || []
                        : analytics?.byManager || []
                    ).map((_row, i) => (
                      <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </Container>
    </Layout>
  );
};

export default AnalyticsPage;
